import { fixMaybeBrokenText } from "@/lib/text-shared";
import type { Product } from "@/lib/catalog";

export type ProductVariantOption = {
  product: Product;
  label: string;
  diameterLabel: string | null;
  diameterSort: number;
  isCurrent: boolean;
};

export type ProductFamilyContext = {
  currentDiameter: string | null;
  familyKey: string;
  variants: ProductVariantOption[];
  relatedProducts: Product[];
  relatedCategoryLabels: string[];
};

const GENERIC_NAME_TOKENS = new Set([
  "bitstone",
  "диск",
  "алмазный",
  "алмазная",
  "алмазные",
  "отрезной",
  "отрезная",
  "отрезные",
  "сегментный",
  "сегментная",
  "лазерный",
  "лазерная",
  "премиум",
  "extra",
  "plus",
  "premium",
  "laser",
  "advanced",
  "technic",
  "мм",
  "mm"
]);

const STABLE_ATTRIBUTE_PATTERNS = [
  /производитель/i,
  /для материала/i,
  /вид алмазного диска/i,
  /тип сегмента/i,
  /тип диска/i,
  /для инструмента/i,
  /^тип$/i
];

function normalizeText(value: string | null | undefined) {
  return fixMaybeBrokenText(value ?? "")
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[()]/g, " ")
    .replace(/[^a-z0-9а-я]+/gi, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function cleanNumericLabel(value: string) {
  const normalized = value.replace(",", ".").trim();
  const numeric = Number(normalized);

  if (Number.isFinite(numeric)) {
    return Number.isInteger(numeric)
      ? String(numeric)
      : numeric.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
  }

  return value.trim();
}

function findAttributeValue(product: Product, patterns: RegExp[]) {
  const attribute = product.attributes.find((item) =>
    patterns.some((pattern) => pattern.test(fixMaybeBrokenText(item.name)))
  );

  return attribute?.value?.trim() || null;
}

function extractDiameterFromText(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const raw = fixMaybeBrokenText(value).replace(",", ".");
  const mmMatch = raw.match(/(\d+(?:\.\d+)?)\s*(?:мм|mm)\b/i);
  if (mmMatch) {
    return cleanNumericLabel(mmMatch[1]);
  }

  const leadingMatch = raw.match(/\b(\d+(?:\.\d+)?)\s*(?:x|х|×|\/)/i);
  if (leadingMatch) {
    const numeric = Number(leadingMatch[1]);
    if (numeric >= 50 && numeric <= 1200) {
      return cleanNumericLabel(leadingMatch[1]);
    }
  }

  return null;
}

export function extractProductDiameter(product: Product) {
  const rawDiameter =
    findAttributeValue(product, [/диаметр наружный/i, /^диаметр$/i, /диаметр диска/i]) ??
    extractDiameterFromText(product.name);

  if (!rawDiameter) {
    return null;
  }

  return rawDiameter.includes("мм") ? rawDiameter : `${rawDiameter} мм`;
}

function getDiameterSortValue(product: Product) {
  const diameter = extractProductDiameter(product);
  if (!diameter) {
    return Number.MAX_SAFE_INTEGER;
  }

  const numeric = Number(diameter.replace(",", ".").match(/\d+(?:\.\d+)?/)?.[0] ?? "");
  return Number.isFinite(numeric) ? numeric : Number.MAX_SAFE_INTEGER;
}

function buildNameSignature(name: string) {
  const cleaned = normalizeText(name)
    .replace(/\b\d+(?:\.\d+)?\s*(?:мм|mm)\b/gi, " ")
    .replace(/\b\d+(?:\.\d+)?\s*(?:x|х|×)\s*\d+(?:\.\d+)?(?:\s*(?:x|х|×)\s*\d+(?:\.\d+)?)?/gi, " ")
    .replace(/\b\d+(?:\.\d+)?\s*\/\s*\d+(?:\.\d+)?(?:\s*x\s*\d+(?:\.\d+)?)?/gi, " ")
    .replace(/\b\d{6,}\b/g, " ");

  const tokens = cleaned
    .split(" ")
    .filter((token) => token && !GENERIC_NAME_TOKENS.has(token) && !/\d/.test(token));

  return tokens.slice(0, 10).join(" ");
}

function buildStableAttributeSignature(product: Product) {
  return product.attributes
    .filter((item) => item.value && STABLE_ATTRIBUTE_PATTERNS.some((pattern) => pattern.test(fixMaybeBrokenText(item.name))))
    .map((item) => `${normalizeText(item.name)}:${normalizeText(item.value)}`)
    .filter(Boolean)
    .slice(0, 4)
    .join("|");
}

export function buildProductFamilyKey(product: Product) {
  if (product.variantGroup?.trim()) {
    return `manual::${normalizeText(product.variantGroup)}`;
  }

  const categoryKey = product.categoryFullSlug ?? product.categorySlug ?? "catalog";
  const nameKey = buildNameSignature(product.name) || normalizeText(product.name);
  const attributeKey = buildStableAttributeSignature(product);

  return [categoryKey, nameKey, attributeKey].filter(Boolean).join("::");
}

function collectSearchTokens(product: Product) {
  const baseTokens = normalizeText(product.name).split(" ");

  for (const item of product.attributes) {
    const combined = `${fixMaybeBrokenText(item.name)} ${fixMaybeBrokenText(item.value)}`.trim();
    if (!combined.length) {
      continue;
    }

    baseTokens.push(...normalizeText(combined).split(" "));
  }

  return new Set(
    baseTokens.filter((token) => token.length > 2 && !GENERIC_NAME_TOKENS.has(token) && !/^\d+$/.test(token))
  );
}

function getParentBranch(fullSlug: string | null) {
  if (!fullSlug) {
    return null;
  }

  const parts = fullSlug.split("/");
  return parts.length > 1 ? parts.slice(0, -1).join("/") : fullSlug;
}

function getTopBranch(fullSlug: string | null) {
  return fullSlug?.split("/")[0] ?? null;
}

export function buildProductFamilyContext(
  currentProduct: Product,
  allProducts: Product[],
  categoryNameBySlug = new Map<string, string>()
): ProductFamilyContext {
  const currentFamilyKey = buildProductFamilyKey(currentProduct);
  const currentTokens = collectSearchTokens(currentProduct);
  const currentMaterial = normalizeText(findAttributeValue(currentProduct, [/для материала/i]));
  const currentProducer = normalizeText(findAttributeValue(currentProduct, [/производитель/i]));
  const currentParentBranch = getParentBranch(currentProduct.categoryFullSlug);
  const currentTopBranch = getTopBranch(currentProduct.categoryFullSlug);

  const variants = allProducts
    .filter((product) => buildProductFamilyKey(product) === currentFamilyKey)
    .map((product) => {
      const diameterLabel = extractProductDiameter(product);
      return {
        product,
        label: diameterLabel ?? product.sku ?? `SKU ${product.id}`,
        diameterLabel,
        diameterSort: getDiameterSortValue(product),
        isCurrent: product.id === currentProduct.id
      } satisfies ProductVariantOption;
    })
    .sort((a, b) => {
      if (a.diameterSort !== b.diameterSort) {
        return a.diameterSort - b.diameterSort;
      }

      return a.label.localeCompare(b.label, "ru");
    });

  const relatedProducts = allProducts.filter((product) => {
    if (product.id === currentProduct.id) {
      return false;
    }

    const productTokens = collectSearchTokens(product);
    const overlap = Array.from(currentTokens).filter((token) => productTokens.has(token)).length;
    const productMaterial = normalizeText(findAttributeValue(product, [/для материала/i]));
    const productProducer = normalizeText(findAttributeValue(product, [/производитель/i]));
    const productParentBranch = getParentBranch(product.categoryFullSlug);
    const productTopBranch = getTopBranch(product.categoryFullSlug);

    const sameContext =
      (currentProducer && currentProducer === productProducer) ||
      (currentMaterial && currentMaterial === productMaterial) ||
      (currentParentBranch && currentParentBranch === productParentBranch) ||
      (currentTopBranch && currentTopBranch === productTopBranch);

    return sameContext && overlap >= 2;
  });

  const relatedCategoryLabels = Array.from(
    new Set(
      relatedProducts
        .map((product) => {
          const slug = product.categorySlug ?? "";
          return categoryNameBySlug.get(slug) || null;
        })
        .filter(Boolean) as string[]
    )
  );

  return {
    currentDiameter: extractProductDiameter(currentProduct),
    familyKey: currentFamilyKey,
    variants,
    relatedProducts,
    relatedCategoryLabels
  };
}
